/**
 * Performance monitoring module for Mapbox GL render cycles.
 *
 * Tracks render performance metrics by measuring the duration between
 * 'map-x-render-start' and 'map-x-render' events. Accumulates samples over 500ms intervals
 * to calculate average render time and effective FPS.
 *
 * Sample Rate: 0.09% of render cycles are reported to analytics
 *
 * Performance Calculation:
 * 1. Capture render durations between map-x-render-start/map-x-render events
 * 2. Aggregate samples over 500ms collection window
 * 3. Calculate average render time from collected samples
 * 4. Convert to FPS (1000ms / avgRenderTime)
 *
 * Example Sequence:
 * ```
 * t=0ms:    map-x-render-start  | Frame 1 begins
 * t=16.7ms: map-x-render | Frame 1 complete (duration: 16.7ms)
 * t=33.3ms: map-x-render-start  | Frame 2 begins
 * t=50.0ms: map-x-render | Frame 2 complete (duration: 16.7ms)
 * t=500ms:  emit event   | Analytics payload (if sampled):
 *                        | avgRenderTime: 16.7ms
 *                        | fps: 60 (1000/16.7)
 * ```
 *
 * @module viewer-report
 */

import random from 'lodash/random'
import { computed } from 'vue'
import { getItem } from '@/plugins/storage'

let renderStartTime = null
let lastMeasureTime = performance.now()
let renderDurations = []

/**
 * Initializes timing for a new render cycle.
 * Invoked by Map-X's 'map-x-render-start' event to establish
 * the baseline timestamp for the current frame's render duration.
 */
export const handleRenderStart = () => {
  renderStartTime = performance.now()
}

const isFpsEnabled = computed(() => {
  return getItem('fpsMonitor')
})

/**
 * Processes render completion and manages performance metrics.
 *
 * Accumulates render durations and, at 500ms intervals, computes
 * aggregate performance metrics. Emits analytics event containing
 * average render time and calculated FPS.
 *
 * @param {Function} emit - Analytics event emitter callback
 * @fires map-event
 *
 * @example
 * // Sample analytics payload after 500ms collection:
 * {
 *   type: 'event',
 *   name: 'map_viewport_changed',
 *   time_to_render: 16,    // Average render time in ms
 *   interaction_fps: 60    // Calculated frames per second
 * }
 */
export const reportRender = (emit) => {
  // only run the calculations if in the sample group or if the FPS monitor is enabled
  if (random(1, 10_000) > 9 /* 0.09% sample rate */ && !isFpsEnabled.value) {
    return
  }

  const now = performance.now()
  const timeSinceMeasure = now - lastMeasureTime
  const timeToWait = 500

  if (renderStartTime !== null) {
    renderDurations.push(now - renderStartTime)
    renderStartTime = null
  }

  if (timeSinceMeasure <= timeToWait) {
    return
  }

  // Reset timer and bail if no render data
  lastMeasureTime = now
  if (!renderDurations.length) {
    renderDurations = []
    return
  }

  const avgRenderTime =
    renderDurations.reduce((x, y) => x + y, 0) / renderDurations.length
  const fps = Math.round(1000 / avgRenderTime)

  renderDurations = []

  emit('fps-measured', fps)

  // only send the marketing event if the FPS monitor is disabled
  if (!isFpsEnabled.value) {
    emit('map-event', {
      type: 'event',
      name: 'map_viewport_changed',
      time_to_render: Math.round(avgRenderTime),
      interaction_fps: fps,
    })
  }
}
